import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, FieldCheckbox, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';
import { USER_TYPE } from '../../../util/enums';

const SELLER_AGREEMENT_URL = 'https://drive.google.com/file/d/1SrAGR7jRPGKzlpAIOGncDwKB55huWo7d/view?pli=1';
const BUYER_AGREEMENT_URL = 'https://drive.google.com/file/d/1Grmz0x3AOdBN5QR2SGbUDM5Pe2SnSzAY/view?usp=sharing';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{
      userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) || USER_TYPE.PROVIDER,
    }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        preselectedUserType,
        userTypes,
        userFields,
        values,
      } = formRenderProps;

      const { userType } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );

      // password
      const passwordRequiredMessage = intl.formatMessage({
        id: 'SignupForm.passwordRequired',
      });
      const passwordMinLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooShort',
        },
        {
          minLength: validators.PASSWORD_MIN_LENGTH,
        }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        {
          id: 'SignupForm.passwordTooLong',
        },
        {
          maxLength: validators.PASSWORD_MAX_LENGTH,
        }
      );
      const passwordMinLength = validators.minLength(
        passwordMinLengthMessage,
        validators.PASSWORD_MIN_LENGTH
      );
      const passwordMaxLength = validators.maxLength(
        passwordMaxLengthMessage,
        validators.PASSWORD_MAX_LENGTH
      );
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
      const passwordValidators = validators.composeValidators(
        passwordRequired,
        passwordMinLength,
        passwordMaxLength
      );

      // Custom user fields. Since user types are not supported here,
      // only fields with no user type id limitation are selected.
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const userAgreementRequired = value => {
        const isAccepted = Array.isArray(value) ? value.includes('accepted') : (value === true || value === 'accepted');
        return isAccepted ? undefined : intl.formatMessage({ id: 'SignupForm.userAgreementRequired' });
      };

      const showUserAgreement = showDefaultUserFields && userType === USER_TYPE.PROVIDER;

      const userAgreementLabel = showUserAgreement ? (
        <>
          I acknowledge that by creating this account, I am entering into a legally binding agreement with Balloon Artist Marketplace and agree to be bound by the{' '}
          <a href={SELLER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Seller Agreement
          </a>
          {' '}and{' '}
          <a href={SELLER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Legal Terms
          </a>
          . I understand that all digital purchases are final and that my use of purchased files is governed by the selected license and the{' '}
          <a href={BUYER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Buyer Agreement
          </a>
          {' '}and{' '}
          <a href={BUYER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer" className={css.userAgreementLink}>
            Legal Terms
          </a>
          .
        </>
      ) : null;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'SignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.emailPlaceholder',
                })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.firstNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'SignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.lastNamePlaceholder',
                  })}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'SignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <FieldTextInput
                className={css.password}
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="new-password"
                label={intl.formatMessage({
                  id: 'SignupForm.passwordLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'SignupForm.passwordPlaceholder',
                })}
                validate={passwordValidators}
              />

              <FieldTextInput
                className={css.row}
                type="text"
                id={formId ? `${formId}.instagram` : 'instagram'}
                name="instagram"
                label={intl.formatMessage({ id: 'SignupForm.instagramLabel' })}
                placeholder="https://instagram.com/yourprofile"
                validate={validators.urlRequired(
                  intl.formatMessage({ id: 'SignupForm.validInstagramUrl' })
                )}
              />

              <FieldTextInput
                className={css.row}
                type="text"
                name="website"
                id={formId ? `${formId}.website` : 'website'}
                label={intl.formatMessage({ id: 'SignupForm.websiteLabel' })}
                placeholder="https://www.yourwebsite.com"
                validate={validators.urlRequired(
                  intl.formatMessage({ id: 'SignupForm.validWebsiteUrl' })
                )}
              />

              <FieldTextInput
                className={css.row}
                type="text"
                id={formId ? `${formId}.taxId` : 'taxId'}
                name="taxId"
                label="Tax ID"
                placeholder="Enter your Tax ID"
                validate={validators.required('Tax ID is required')}
              />

              <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
            </div>
          ) : null}

          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          ) : null}

          {showUserAgreement ? (
            <div className={css.userAgreement}>
              <FieldCheckbox
                id={formId ? `${formId}.userAgreementAccepted` : 'userAgreementAccepted'}
                name="userAgreementAccepted"
                value="accepted"
                label={userAgreementLabel}
                validate={userAgreementRequired}
              />
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
